import { isCumulativeOptionGuard } from '#domain/core/filter/components/periodSelector/periodSelectorUtils.ts';
import { isCustomDateEquals, parseCustomDate } from '#helpers';
import { useFiltersStore } from '#stores/filters.store';
import { useUserStore } from '#stores/user.store';
import {
  isAValidNielsenPeriod,
  isDateSelectionOption,
  isNielsenInterval,
  isNielsenPeriod,
  isPeriod,
  type ComparisonPeriodType,
  type DatePeriod,
  type DatePeriodIntervalType,
  type DateSelectionOption,
  type NielsenPeriodIntervalType,
  type PeriodIntervalType
} from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { KpisPerimeterType } from '../kpisPerimeter.model';

export const usePeriod = (): KpisPerimeterType['period'] & {
  generateSelectedPeriodFilter: (
    period: DatePeriodIntervalType | NielsenPeriodIntervalType
  ) => DatePeriodIntervalType | NielsenPeriodIntervalType | undefined;
} => {
  const userStore = useUserStore();
  const { defaultKpisPerimeter } = storeToRefs(userStore);

  const selectedPeriod = useGetPeriodFromFilter();
  const comparisonPeriod = useGetComparisonPeriodFromFilter();

  const { setFilter } = useFiltersStore();

  const isDefault = (
    currentValue: DatePeriodIntervalType | NielsenPeriodIntervalType = selectedPeriod.value,
    currentComparisonValue: ComparisonPeriodType = comparisonPeriod.value
  ) => {
    const checksForEquality = [
      currentValue.option === defaultKpisPerimeter.value.period.option,
      currentValue.period === defaultKpisPerimeter.value.period.period,
      currentComparisonValue.comparisonMode === 'previousYear'
    ];

    if (isNielsenPeriod(currentValue)) {
      checksForEquality.push(
        currentValue.periodInterval.start === defaultKpisPerimeter.value.period.periodInterval.start,
        currentValue.periodInterval.end === defaultKpisPerimeter.value.period.periodInterval.end
      );
      // Should alway be true because, at the moment,
      // defaultKpisPerimeter.value.period is of type DatePeriodIntervalType
    } else if (!isNielsenPeriod(defaultKpisPerimeter.value.period)) {
      checksForEquality.push(
        isCustomDateEquals(currentValue.periodInterval.start, defaultKpisPerimeter.value.period.periodInterval.start),
        isCustomDateEquals(currentValue.periodInterval.end, defaultKpisPerimeter.value.period.periodInterval.end)
      );
    }

    return checksForEquality.every(Boolean);
  };

  const generateSelectedPeriodFilter = (
    period: DatePeriodIntervalType | NielsenPeriodIntervalType
  ): DatePeriodIntervalType | NielsenPeriodIntervalType => {
    if (!isCumulativeOptionGuard(period.option)) {
      throw new Error('Invalid period option');
    }
    if (isNielsenPeriod(period) && isNielsenInterval(period.periodInterval)) {
      return {
        option: period.option,
        period: period.period,
        periodInterval: period.periodInterval
      } satisfies NielsenPeriodIntervalType;
    }
    if (!isNielsenPeriod(period) && !isNielsenInterval(period.periodInterval)) {
      return {
        option: period.option,
        period: period.period,
        periodInterval: period.periodInterval
      } satisfies DatePeriodIntervalType;
    }
    throw new Error('Invalid period interval');
  };
  const updatePeriod = (
    period: DatePeriodIntervalType | NielsenPeriodIntervalType,
    comparisonPeriod?: ComparisonPeriodType
  ) => {
    setFilter('selectedPeriod', generateSelectedPeriodFilter(period));
    if (comparisonPeriod) {
      setFilter('comparisonPeriod', comparisonPeriod);
    }
    setFilter('referentialIntervals', referentialIntervals.value);
  };

  const resetPeriod = () => {
    setFilter('selectedPeriod', generateSelectedPeriodFilter(defaultKpisPerimeter.value.period));
    setFilter('comparisonPeriod', { comparisonMode: 'previousYear' });
  };

  const referentialIntervals = computed(() => {
    return defaultKpisPerimeter.value.referentialIntervals;
  });

  return {
    referentialIntervals,
    selectedPeriod,
    comparisonPeriod,
    updatePeriod,
    resetPeriod,
    isDefault,
    generateSelectedPeriodFilter
  };
};

const useGetComparisonPeriodFromFilter = () => {
  const filtersStore = useFiltersStore();
  const { filters } = storeToRefs(filtersStore);

  return computed((): ComparisonPeriodType => {
    return filters.value.comparisonPeriod ?? { comparisonMode: 'previousYear' };
  });
};

const useGetPeriodFromFilter = () => {
  const userStore = useUserStore();
  const { defaultKpisPerimeter } = storeToRefs(userStore);
  const filtersStore = useFiltersStore();
  const { filters } = storeToRefs(filtersStore);

  return computed(() => {
    const selectedPeriod = filters.value.selectedPeriod ?? defaultKpisPerimeter.value.period;
    let option: DateSelectionOption = selectedPeriod.option;
    let period = selectedPeriod.period as DatePeriod;
    let periodInterval = selectedPeriod.periodInterval as PeriodIntervalType;

    if (isDateSelectionOption(filters.value.selectedPeriod?.option)) {
      option = filters.value.selectedPeriod?.option as DateSelectionOption;
    }

    if (isPeriod(filters.value.selectedPeriod?.period)) {
      if (filters.value.selectedPeriod?.period !== 'nielsen') {
        period = filters.value.selectedPeriod?.period as DatePeriod;
      }
    }

    if (
      typeof filters.value.selectedPeriod?.periodInterval.start === 'string' &&
      typeof filters.value.selectedPeriod?.periodInterval.end === 'string'
    ) {
      if (
        isAValidNielsenPeriod(filters.value.selectedPeriod.periodInterval.start) &&
        isAValidNielsenPeriod(filters.value.selectedPeriod.periodInterval.end)
      ) {
        return {
          period: 'nielsen',
          option,
          periodInterval: {
            start: filters.value.selectedPeriod.periodInterval.start,
            end: filters.value.selectedPeriod.periodInterval.end
          }
        } satisfies NielsenPeriodIntervalType;
      }

      periodInterval = {
        start: parseCustomDate(filters.value.selectedPeriod.periodInterval.start),
        end: parseCustomDate(filters.value.selectedPeriod.periodInterval.end)
      };
    }

    return {
      period,
      option,
      periodInterval
    } satisfies DatePeriodIntervalType;
  });
};
